import {
  AuthError,
  type AuthTokens,
  type MeProfile,
  type SessionBroker,
} from '../contract/auth.js';

/**
 * Authenticated GiftMotor API client. Holds tokens in MEMORY only, schedules
 * proactive refresh ahead of expiry, and retries exactly once through the
 * broker on a 401 (an expired access token), keeping normal API calls on the
 * existing Cognito JWT authorizer path.
 */
export class ApiClient {
  private tokens: AuthTokens | undefined;
  private refreshTimer: ReturnType<typeof setTimeout> | undefined;
  private refreshing: Promise<AuthTokens | undefined> | undefined;

  constructor(
    private readonly apiBase: string,
    private readonly broker: SessionBroker,
    private readonly onSessionLost: () => void,
  ) {}

  get signedIn(): boolean {
    return this.tokens !== undefined;
  }

  setTokens(tokens: AuthTokens): void {
    this.tokens = tokens;
    this.scheduleRefresh(tokens.expiresIn);
  }

  clear(): void {
    this.tokens = undefined;
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    this.refreshTimer = undefined;
  }

  private scheduleRefresh(expiresIn: number): void {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    // Refresh two minutes early; never sooner than 30s out.
    const delay = Math.max(30, expiresIn - 120) * 1000;
    this.refreshTimer = setTimeout(() => {
      void this.refreshNow();
    }, delay);
  }

  /** Single-flight broker refresh (multi-tab-safe on the server side). */
  private refreshNow(): Promise<AuthTokens | undefined> {
    this.refreshing ??= this.broker
      .refresh()
      .then((tokens) => {
        if (tokens) this.setTokens(tokens);
        else {
          this.clear();
          this.onSessionLost();
        }
        return tokens;
      })
      .catch(() => {
        // Network trouble: keep current tokens; the schedule or a 401 retries.
        return this.tokens;
      })
      .finally(() => {
        this.refreshing = undefined;
      });
    return this.refreshing;
  }

  /** Boot-time restore: cookie → tokens, or signed-out. */
  async restore(): Promise<boolean> {
    const tokens = await this.broker.refresh();
    if (!tokens) return false;
    this.setTokens(tokens);
    return true;
  }

  async signOut(): Promise<void> {
    this.clear();
    await this.broker.signOut();
  }

  async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const res = await this.rawRequest(method, path, body, true);
    if (res.status === 403) {
      throw new AuthError('SUSPENDED', 'This account is not currently available.');
    }
    if (!res.ok) {
      throw new AuthError('UNKNOWN', `Request failed (${res.status})`);
    }
    return (await res.json()) as T;
  }

  private async rawRequest(
    method: string,
    path: string,
    body: unknown,
    retryOn401: boolean,
  ): Promise<Response> {
    if (!this.tokens) throw new AuthError('UNKNOWN', 'Not signed in');
    let res: Response;
    try {
      res = await fetch(`${this.apiBase}${path}`, {
        method,
        headers: {
          authorization: `Bearer ${this.tokens.accessToken}`,
          ...(body !== undefined && { 'content-type': 'application/json' }),
        },
        ...(body !== undefined && { body: JSON.stringify(body) }),
      });
    } catch {
      throw new AuthError('NETWORK', 'Network problem. Check your connection and try again.');
    }
    if (res.status === 401 && retryOn401) {
      const refreshed = await this.refreshNow();
      if (!refreshed) {
        throw new AuthError('UNKNOWN', 'Your session ended. Sign in again.');
      }
      return this.rawRequest(method, path, body, false);
    }
    return res;
  }

  me(): Promise<MeProfile> {
    return this.request<MeProfile>('GET', '/me');
  }
}
